import React from 'react'
import ResolveProductPricing from 'Utils/ResolveProductPricing'

import { Map } from './Static/PerRowMap'

const TrackingContext = React.createContext()
const ctrPostInterval = 10000

const impressionsBuffer = {
  buffer: [],
  getCount() {
    return this.buffer.length
  },
  addImpressionToQueue({ item_id, ...rest }) {
    const haveSame = this.buffer.some(item => item.item_id === item_id)

    if (!haveSame) {
      this.buffer.push({ item_id, ...rest })
    }
  },
  flushImpressions(count) {
    const itemsToTrack = this.buffer.slice(0, count)

    this.buffer.splice(0, count)

    return itemsToTrack
  },
}

Object.freeze(impressionsBuffer)

export class TrackingProvider extends React.Component {
  constructor(props) {
    super(props)

    this.itemIdsBuffer = []
    this.trackedItems = []
    this.onImpression = this.onImpression.bind(this)
    this.onClick = this.onClick.bind(this)
    this.postCtrImpressions = this.postCtrImpressions.bind(this)
    this.getSimilarItemPerRow = this.getSimilarItemPerRow.bind(this)
    this.prepareTrackViewItemList = this.prepareTrackViewItemList.bind(this)
    this.onAddToCart = this.onAddToCart.bind(this)

    if (this.props.withCtrTracking && typeof window !== 'undefined') {
      setTimeout(this.postCtrImpressions, ctrPostInterval)

      window.addEventListener('beforeunload', () => {
        this.postCtrImpressions()

        return undefined
      })
    }
  }

  postCtrImpressions() {
    if (this.itemIdsBuffer.length > 0) {
      axios.post('/items/track-impressions', {
        item_ids: this.itemIdsBuffer,
        url: window.location.href,
        per_page: this.props.perPage,
      }).catch((error) => {})

      this.itemIdsBuffer = []
    }

    setTimeout(this.postCtrImpressions, ctrPostInterval)
  }

  getSimilarItemPerRow(listType) {
    const width = window.innerWidth
    const matchingMap = Map[listType] || Map.default

    const breakpoints = Object.keys(matchingMap)
      .filter(bp => bp !== 'default')
      .sort((a, b) => b - a)

    const matchingBreakpoint = breakpoints.find((bp) => width >= Number(bp))
    const perRow = matchingMap[matchingBreakpoint] || matchingMap.default

    return perRow
  }

  prepareTrackViewItemList(attributes, discount, price) {
    const item = {
      item_id: String(attributes.id),
      item_name: attributes.title,
      affiliation: attributes.affiliation || '',
      currency: 'USD',
      index: parseInt(attributes.index, 10),
      item_brand: attributes.itemBrand,
      item_category: attributes.itemCategory,
      item_category2: attributes.subcategory || attributes.itemCategory2,
      item_category3: attributes.itemCategory3,
      item_category4: attributes.itemCategory4,
      item_category5: attributes.itemCategory5,
      item_list_id: attributes.searchQuery || attributes.itemListId,
      item_list_name: attributes.searchQuery || attributes.itemListName,
      item_variant: attributes.productType,
      location_id: attributes.locationId && attributes.locationId.join("_"),
      price: parseFloat(price),
      quantity: 1,
      ...(discount > 0 ? { discount: parseFloat(discount) } : {}),
      ...(window.itemListEnrichmentType ? { item_list_enrichment_type: window.itemListEnrichmentType } : {}),
    }

    Object.keys(item).forEach((key) => {
      if (item[key] === undefined) {
        item[key] = null
      }
    })

    return item
  }

  onAddToCart(item, index, gaList, cart) {
    console.log('onAddToCart', item, index, gaList, cart)
    const { trackingData } = this.props
    const matchingItem = trackingData.find((trackingItem) => trackingItem.itemId === Number(item.id))
    const pricing = ResolveProductPricing({ cart, attributes: item.attributes, id: item.id })
    const newAttributes = {
      ...item.attributes,
      ...matchingItem,
      index,
      itemListId: gaList,
      itemListName: gaList,
    }
    const discount = (pricing.originalPrice - pricing.price).toFixed(2)
    const itemData = this.prepareTrackViewItemList(newAttributes, discount, pricing.price)
    // take user id from APP.config.userId - for slow application
    // or from window.pageConfig.currentUserId - for fast application
    const userId = window.App?.config?.userId
      || window.pageConfig?.currentUserId
      || ''
    const eventSource = gaList === 'Wish List' ? 'wishlist icon' : 'model card'

    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'add_to_cart',
        user_id: userId,
        event_source: eventSource,
        ecommerce: {
          currency: 'USD',
          value: itemData.price,
          items: [itemData]
        },
        _clear: true,
      })
    }
  }

  onImpression(item, index, gaList, cart) {
    const { trackingData } = this.props
    const matchingItem = trackingData.find((trackingItem) => trackingItem.itemId === Number(item.id))

    const pricing = ResolveProductPricing({ cart, attributes: item.attributes, id: item.id })

    const newAttributes = {
      ...item.attributes,
      ...matchingItem,
      index,
      itemListId: gaList,
      itemListName: gaList,
    }

    const itemData = this.prepareTrackViewItemList(newAttributes, pricing.discountAmount.toFixed(2), pricing.price)
    impressionsBuffer.addImpressionToQueue(itemData)
    const itemsPerRow = this.getSimilarItemPerRow(gaList)

    if (impressionsBuffer.getCount() >= itemsPerRow
      || this.trackedItems.length === trackingData.length - 1
    ) {
      const itemsToTrack = impressionsBuffer.flushImpressions(itemsPerRow)

      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'view_item_list',
          user_id: window.App?.config?.userId || window.pageConfig?.currentUserId || '',
          ecommerce: {
            item_list_id: gaList,
            item_list_name: gaList,
            items: itemsToTrack,
          },
          _clear: true,
        })
      }
    }

    this.trackedItems.push(item.id)

    if (this.props.withCtrTracking) {
      this.itemIdsBuffer.push(item.id)
    }
  }

  onClick(item, index, gaList, cart) {
    const { trackingData } = this.props
    const matchingItem = trackingData.find((trackingItem) => trackingItem.itemId === Number(item.id))
    const pricing = ResolveProductPricing({ cart, attributes: item.attributes, id: item.id })
    const newAttributes = {
      ...item.attributes,
      ...matchingItem,
      index,
      itemListId: gaList,
      itemListName: gaList,
    }
    const discount = (pricing.originalPrice - pricing.price).toFixed(2)
    const itemData = this.prepareTrackViewItemList(newAttributes, discount, pricing.price)
    // take user id from APP.config.userId - for slow application
    // or from window.pageConfig.currentUserId - for fast application
    const userId = window.App?.config?.userId
      || window.pageConfig?.currentUserId
      || ''

    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'select_item',
        user_id: userId,
        ecommerce: {
          currency: 'USD',
          value: itemData.price,
          item_list_name: itemData.item_list_name,
          item_list_id: itemData.item_list_id,
          items: [itemData]
        },
        _clear: true,
      })
    }
  }

  render() {
    return (
      <TrackingContext.Provider
        value={{
          onImpression: this.onImpression,
          onClick: this.onClick,
          onAddToCart: this.onAddToCart,
        }}
      >
        {this.props.children}
      </TrackingContext.Provider>
    )
  }
}

export const TrackingConsumer = TrackingContext.Consumer
